'use client';

import type React from 'react';
import { useState, useRef, useCallback } from 'react';
import {
  X,
  Upload,
  ImageIcon,
  Video,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

const cn = (...classes: (string | boolean | undefined)[]) =>
  classes.filter(Boolean).join(' ');

type DeleteImageArgs = {
  index: number;
  productId?: string | number;
  imageUrl?: string;
};

interface UploadImagensProps {
  filesImage: File[];
  previewFiles: string[];
  FileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  productId?: string | number;
  onDeleteFile: (args: DeleteImageArgs) => void;
}

export const UploadImagens: React.FC<UploadImagensProps> = ({
  filesImage,
  previewFiles,
  FileUpload,
  productId,
  onDeleteFile,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasError = filesImage.length > 5;
  const hasFiles = previewFiles.length > 0;
  const videoCount = filesImage.filter((f) =>
    f.type.startsWith('video/')
  ).length;
  const imageCount = filesImage.filter((f) =>
    f.type.startsWith('image/')
  ).length;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const syntheticEvent = {
          target: { files },
          currentTarget: { files },
        } as React.ChangeEvent<HTMLInputElement>;

        FileUpload(syntheticEvent);
      }
    },
    [FileUpload]
  );

  const handleClick = () => fileInputRef.current?.click();

  const getFileType = (file: File): 'image' | 'video' =>
    file.type.startsWith('video/') ? 'video' : 'image';

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-100 to-red-50 border border-red-200">
            <ImageIcon className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
            Imagens do Produto
          </h2>
        </div>
        <p className="text-muted-foreground text-base ml-[60px]">
          Adicione imagens e vídeos para destacar o produto
        </p>
      </div>

      <div className="bg-card rounded-2xl border-2 border-border shadow-lg hover:shadow-xl transition-shadow duration-300 p-8 space-y-8">
        <div className="bg-muted/30 rounded-xl p-5 border border-border">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                Requisitos de Upload
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  Até{' '}
                  <span className="font-medium text-foreground">5 imagens</span>{' '}
                  ou{' '}
                  <span className="font-medium text-foreground">
                    4 imagens + 1 vídeo
                  </span>
                </li>
                <li>
                  Tamanho recomendado:{' '}
                  <span className="font-medium text-foreground">
                    800px - 1600px
                  </span>
                </li>
                <li>Formatos: JPG, PNG, WebP, MP4</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'relative rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer',
            isDragging
              ? 'border-red-500 bg-red-500/5 scale-[1.01]'
              : hasError
                ? 'border-destructive bg-destructive/5'
                : 'border-border bg-background hover:border-red-500/40 hover:bg-accent/30'
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={FileUpload}
            className="sr-only"
          />

          <div className="p-10 text-center space-y-3">
            <div className="flex justify-center">
              <div
                className={cn(
                  'w-16 h-16 rounded-full flex items-center justify-center',
                  isDragging ? 'bg-red-100' : 'bg-muted'
                )}
              >
                <Upload
                  className={cn(
                    'w-8 h-8',
                    isDragging ? 'text-red-600' : 'text-muted-foreground'
                  )}
                />
              </div>
            </div>
            <p className="text-base font-medium text-foreground">
              {isDragging
                ? 'Solte os arquivos aqui'
                : 'Arraste e solte seus arquivos'}
            </p>
            <p className="text-sm text-muted-foreground">
              ou{' '}
              <span className="text-red-600 font-semibold hover:underline">
                clique para selecionar
              </span>
            </p>

            {hasError && (
              <div className="flex items-center justify-center gap-2 text-red-600 mt-4">
                <AlertCircle className="w-4 h-4" />
                <p className="text-sm font-medium">
                  Máximo de 5 arquivos permitidos
                </p>
              </div>
            )}
          </div>
        </div>

        {hasFiles && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-600">
              <CheckCircle2 className="w-4 h-4" />
              <p className="text-sm font-medium text-foreground">
                {previewFiles.length}{' '}
                {previewFiles.length === 1 ? 'arquivo' : 'arquivos'}{' '}
                selecionado(s)
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {imageCount > 0 && (
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3.5 h-3.5" /> {imageCount} imagem(ns)
                </span>
              )}
              {videoCount > 0 && (
                <span className="flex items-center gap-1">
                  <Video className="w-3.5 h-3.5" /> {videoCount} vídeo(s)
                </span>
              )}
            </div>
          </div>
        )}

        {hasFiles && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {previewFiles.map((preview, index) => {
              const file = filesImage[index];
              const fileType = file ? getFileType(file) : 'image';

              return (
                <div
                  key={index}
                  className="relative group rounded-xl overflow-hidden border-2 border-border bg-muted hover:border-red-500 hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                >
                  <div className="aspect-square bg-muted">
                    {fileType === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-12 h-12 text-muted-foreground" />
                      </div>
                    ) : (
                      <img
                        src={preview || '/placeholder.svg'}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFile({ index, productId, imageUrl: preview });
                    }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-background/90 backdrop-blur-sm border border-border text-foreground hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-500/30"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-white font-medium truncate">
                        {file?.name || `Arquivo ${index + 1}`}
                      </p>
                      {fileType === 'video' && (
                        <Video className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
